import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import AdminLayout from './admin/AdminLayout';
import { ToastProvider } from './admin/context/ToastContext';
import '../styles/admin.css';

// Views
import DashboardView from './admin/views/DashboardView';
import GymsView from './admin/views/GymsView';
import ClubsView from './admin/views/ClubsView';
import UsuariosView from './admin/views/UsuariosView';
import EntrenadoresView from './admin/views/EntrenadoresView';
import EventosView from './admin/views/EventosView';
import EditarEventosView from './admin/views/EditarEventosView';
import EventoParticipantesView from './admin/views/EventoParticipantesView';
import PrimeTimeView from './admin/views/PrimeTimeView';
import ParticipantesView from './admin/views/ParticipantesView';
import ProductosView from './admin/views/ProductosView';
import AfiliacionesView from './admin/views/AfiliacionesView';
import ValoresAfiliacionView from './admin/views/ValoresAfiliacionView';
import RecordsView from './admin/views/RecordsView';
import MarcasPersonalesView from './admin/views/MarcasPersonalesView';
import RankingsView from './admin/views/RankingsView';
import RecordsLiftingCastView from './admin/views/RecordsLiftingCastView';
import LiftingCastView from './admin/views/LiftingCastView';

const VIEWS = {
  dashboard: DashboardView,
  gyms: GymsView,
  clubs: ClubsView,
  productos: ProductosView,
  usuarios: UsuariosView,
  entrenadores: EntrenadoresView,
  eventos: EventosView,
  participantes: ParticipantesView,
  afiliaciones: AfiliacionesView,
  'valores-afiliacion': ValoresAfiliacionView,
  records: RecordsView,
  marcas: MarcasPersonalesView,
  rankings: RankingsView,
  liftingcast: LiftingCastView,
};

const DEFAULT_VIEW = 'dashboard';

const AdminPanel = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const viewFromUrl = query.get('view');
  const editIdFromUrl = query.get('edit');
  const participantesIdFromUrl = query.get('participantes');
  const primeTimeIdFromUrl = query.get('primetime');
  const liftingcastEventoIdFromUrl = query.get('liftingcast');
  const sincronizarRecordsFromUrl = query.get('sincronizar');

  const [activeView, setActiveView] = useState(
    viewFromUrl || DEFAULT_VIEW
  );

  // 🔁 Sync URL → estado
  useEffect(() => {
    if (viewFromUrl && viewFromUrl !== activeView) {
      setActiveView(viewFromUrl);
    }
  }, [viewFromUrl, activeView]);

  // 🧭 Navegación centralizada
  const handleSetActiveView = (viewKey) => {
    const newQuery = new URLSearchParams();
    newQuery.set('view', viewKey);

    setActiveView(viewKey);
    navigate(`/admin?${newQuery.toString()}`, { replace: true });
  };

  // 🧩 Render dinámico de vistas
  const renderView = () => {
    if (activeView === 'records' && sincronizarRecordsFromUrl === 'liftingcast') {
      return (
        <RecordsLiftingCastView
          onBack={() => navigate('/admin?view=records', { replace: true })}
        />
      );
    }
    if (activeView === 'eventos' && liftingcastEventoIdFromUrl) {
      return (
        <LiftingCastView
          eventoId={liftingcastEventoIdFromUrl}
          onBack={() => {
            const newQuery = new URLSearchParams();
            newQuery.set('view', 'eventos');
            navigate(`/admin?${newQuery.toString()}`, { replace: true });
          }}
        />
      );
    }
    // Prime time del evento: ranking de inscritos por DOTS (hombres y mujeres)
    if (activeView === 'eventos' && primeTimeIdFromUrl) {
      return (
        <PrimeTimeView
          eventoId={primeTimeIdFromUrl}
          onBack={() => navigate(-1)}
        />
      );
    }
    // modo participantes del evento (buscar por RUT, ver afiliación)
    if (activeView === 'eventos' && participantesIdFromUrl) {
      return (
        <EventoParticipantesView
          eventoId={participantesIdFromUrl}
          onBack={() => {
            const newQuery = new URLSearchParams();
            newQuery.set('view', 'eventos');
            navigate(`/admin?${newQuery.toString()}`, { replace: true });
          }}
        />
      );
    }
    // modo edición eventos
    if (activeView === 'eventos' && editIdFromUrl) {
      return (
        <EditarEventosView
          eventoId={editIdFromUrl}
          onBack={() => {
            const newQuery = new URLSearchParams();
            newQuery.set('view', 'eventos');
            navigate(`/admin?${newQuery.toString()}`, { replace: true });
          }}
        />
      );
    }

    const ViewComponent = VIEWS[activeView] || DashboardView;
    return <ViewComponent />;
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('isAdmin');
    navigate('/login');
  };

  return (
    <ToastProvider>
      <AdminLayout
        activeView={activeView}
        setActiveView={handleSetActiveView}
        onLogout={handleLogout}
      >
        {renderView()}
      </AdminLayout>
    </ToastProvider>
  );
};

export default AdminPanel;